import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSearch } from "@/hooks/useSearch";
import { Input } from "@/components/ui/Input";
import { Search } from "lucide-react";

export function GlobalSearchBar() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useSearch(query);

  const results =
    data?.groups.flatMap((g) =>
      g.results.slice(0, 3).map((r) => ({
        id: r.instanceId,
        displayName: r.displayName,
        typeName: g.objectTypeName,
        color: g.color,
      })),
    ) ?? [];

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (query.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="relative w-full max-w-md">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search everything..."
        className="pl-9 h-9"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-surface-overlay shadow-lg">
          {results.slice(0, 5).map((r) => (
            <button
              key={r.id}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent-100 dark:hover:bg-neutral-800"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                navigate(`/entities/${r.id}`);
                setQuery("");
                setOpen(false);
              }}
            >
              {r.color && (
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: r.color }}
                />
              )}
              <span className="truncate font-medium">{r.displayName}</span>
              <span className="ml-auto text-xs text-text-muted">
                {r.typeName}
              </span>
            </button>
          ))}
          <button
            type="button"
            className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-xs text-text-secondary hover:bg-accent-100 dark:hover:bg-neutral-800"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              navigate(`/search?q=${encodeURIComponent(query)}`);
              setOpen(false);
            }}
          >
            View all results for "{query}"
          </button>
        </div>
      )}
    </form>
  );
}
